/**
 * Home's type system over the For Review items: each triage kind belongs to
 * one of the five Home types (Deadlines, Expirations, Agent Activity,
 * Intakes and Requests, Calendar), and each item gets a Linear-style status.
 */
import dayjs from "dayjs";

import { KIND_ORDER, type Kind } from "@/components/ui/kind-icon";
import type { ItemStatus } from "@/components/ui/status-icon";

import type { SignalFace } from "@/features/agentAttention/signalTone";

import type { ExpirationRow, ReminderRow } from "../types";
import type { TriageItem, TriageKind } from "./triageModel";

const KIND_OF: Record<TriageKind, Kind> = {
  overdue: "calendar",
  today: "calendar",
  approval: "agent",
  mention: "agent",
  intake: "intake",
  filing: "deadline",
  expiring: "expiration",
  with_client: "intake"
};

export const homeKindOf = (item: Pick<TriageItem, "kind">): Kind =>
  KIND_OF[item.kind];

/** The chip for each agent outcome; a clean finish asks nothing of you. */
const SIGNAL_CHIP: Record<SignalFace, string> = {
  approval: "Approval",
  question: "Question",
  failed: "Failed",
  problems: "Problems",
  finished: "Finished"
};

export const statusOf = (item: TriageItem): ItemStatus => {
  if (item.overdue) return "late";
  if (item.kind === "with_client") return "waiting";
  if (item.signal === "finished") return "upcoming";
  if (
    item.kind === "approval" ||
    item.kind === "mention" ||
    item.kind === "intake"
  )
    return "needs-you";
  return "upcoming";
};

/** The property chip naming what an item is ("Approval", "Passport"). */
export const chipLabel = (item: TriageItem): string => {
  switch (item.kind) {
    case "overdue":
    case "today":
      return (item.row as ReminderRow).kind === "appointment"
        ? "Appointment"
        : "Reminder";
    case "approval":
      return item.signal ? SIGNAL_CHIP[item.signal] : "Approval";
    case "mention":
      return "Mention";
    case "intake":
    case "with_client":
      return "Intake";
    case "filing":
      return "Filing Deadline";
    default:
      return (item.row as ExpirationRow).label || "Expiration";
  }
};

export type KindGroup = { kind: Kind; rows: TriageItem[] };

/** Every Home type in order, with its items (possibly none). */
export const groupByKind = (items: TriageItem[]): KindGroup[] =>
  KIND_ORDER.map((kind) => ({
    kind,
    rows: items.filter((t) => homeKindOf(t) === kind)
  }));

export type RunwayMark = {
  id: string;
  kind: Kind;
  date: Date;
  /** "Oct 24 · Evidence collection due · Amara Okafor" */
  label: string;
  /** The title alone, for the passed zone's badge. */
  title: string;
  path: string;
};

/** Dated items as runway markers; undated agent activity has no place on it. */
export const runwayMarks = (items: TriageItem[]): RunwayMark[] =>
  items.flatMap((t) => {
    const at = t.due ? dayjs(t.due) : null;
    if (!at || !at.isValid()) return [];
    return [
      {
        id: t.id,
        kind: homeKindOf(t),
        date: at.toDate(),
        label: [at.format("MMM D"), t.title, t.clientName]
          .filter(Boolean)
          .join(" · "),
        title: t.title,
        path: t.path
      }
    ];
  });

/** The soonest item still ahead of `now`, if any. */
export const nextDated = (items: TriageItem[], now: Date): TriageItem | null =>
  items
    .filter((t) => t.due && !t.overdue && dayjs(t.due).isAfter(now))
    .sort((a, b) => Date.parse(a.due ?? "") - Date.parse(b.due ?? ""))[0] ??
  null;

/**
 * Approvals waiting on the same client roll up into one row ("3 Approvals
 * Waiting"); everything else stays a row of its own.
 */
export type RailEntry =
  | { kind: "item"; item: TriageItem }
  | { kind: "approvals"; id: string; items: TriageItem[] };

const isApproval = (t: TriageItem): boolean =>
  t.kind === "approval" && (t.signal ?? "approval") === "approval";

export const rollUp = (rows: TriageItem[]): RailEntry[] => {
  const byClient = new Map<string, TriageItem[]>();
  rows.forEach((t) => {
    if (!isApproval(t)) return;
    const key = t.clientId ?? t.clientName;
    byClient.set(key, [...(byClient.get(key) ?? []), t]);
  });
  const seen = new Set<string>();
  const out: RailEntry[] = [];
  rows.forEach((t) => {
    const group = isApproval(t)
      ? byClient.get(t.clientId ?? t.clientName)
      : null;
    if (!group || group.length < 2) {
      out.push({ kind: "item", item: t });
      return;
    }
    const key = t.clientId ?? t.clientName;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ kind: "approvals", id: `approvals:${key}`, items: group });
  });
  return out;
};
